import {
  ApiResponse,
  Curriculum,
  UpdateCurriculumPayload,
  UpdateCurriculumPayloadSchema,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { useTranslation } from "react-i18next";

import { LoadingModal } from "@/pages/LoadingModal/LoadingModal";

import { GenericCurriculumModal } from "../GenericCurriculumModal/GenericCurriculumModal";

import { SchoolEditCurriculumModal } from "./components/SchoolEditCurriculumModal/SchoolEditCurriculumModal";
import { EDIT_CURRICULUM_MODAL_NS } from "./EditCurriculumModal.i18n";

export const EditCurriculumModal = ({
  curriculum,
  error,
  isLoading = false,
  isSchoolAccount = false,
  onHandleSubmit,
  open,
  setOpen,
}: {
  isSchoolAccount?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  curriculum?: Curriculum | null;
  isLoading?: boolean;
  onHandleSubmit: (params: {
    id: string;
    payload: UpdateCurriculumPayload;
  }) => ApiResponse<UpdateCurriculumPayload>;
  error: Error | null;
}) => {
  const { t } = useTranslation(EDIT_CURRICULUM_MODAL_NS);

  const { click } = useTwinklAnalytics();

  const handleFormSubmit = async (data: UpdateCurriculumPayload) => {
    if (!curriculum) {
      return new Error("Curriculum is required for editing.");
    }
    const result = UpdateCurriculumPayloadSchema.safeParse(data);

    if (result.success) {
      return await onHandleSubmit({
        id: curriculum?.id,
        payload: result.data,
      });
    }

    console.error("Form data validation failed:", result.error);

    return result.error;
  };

  // NOTE: Very important for the school version of the modal. Stops the defaults being populated before the data is loaded.
  if (isLoading) {
    return (
      <LoadingModal open={open} width="md" onClose={() => setOpen(false)} />
    );
  }

  const onClickClose = () => {
    click("click_edit_modal_cta", {
      ctaCopy: "Cancel",
    });
  };

  const onPostSave = () => {
    click("click_edit_modal_cta", {
      ctaCopy: "Save Changes",
      clickLocation: "Edit Curriculum Modal",
    });
  };

  return isSchoolAccount ? (
    <SchoolEditCurriculumModal
      curriculum={curriculum}
      error={error}
      isLoading={isLoading}
      open={open}
      setOpen={setOpen}
      onClickClose={onClickClose}
      onPostSave={onPostSave}
      onSubmit={handleFormSubmit}
    />
  ) : (
    <GenericCurriculumModal
      curriculum={curriculum}
      error={error}
      loading={isLoading}
      open={open}
      saveActionLabel={t("actions.save")}
      setOpen={setOpen}
      title={t("title")}
      onClickClose={onClickClose}
      onPostSave={onPostSave}
      onSubmit={handleFormSubmit}
    />
  );
};
